import React, { useEffect, useRef, useState } from 'react';

import {
	ChevronDown,
	Download,
	File,
	FileSpreadsheet,
	FileText,
} from 'lucide-react';

import { ExportService } from '../utils/exportService';

import { Button } from './common';

import type { UnifiedAnalysisData } from '../types';

interface ExportButtonProps {
	analysisData: UnifiedAnalysisData;
	fileId?: string; // Optional file ID for backend PDF export
	className?: string;
	viewMode?: 'default' | 'user_edits' | 'customized_quote'; // View mode for PDF export
}

const ExportButton: React.FC<ExportButtonProps> = ({
	analysisData,
	fileId,
	className = '',
	viewMode = 'default',
}) => {
	const [isOpen, setIsOpen] = useState(false);
	const dropdownRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			if (
				dropdownRef.current &&
				!dropdownRef.current.contains(event.target as Node)
			) {
				setIsOpen(false);
			}
		};

		if (isOpen) {
			document.addEventListener('mousedown', handleClickOutside);
		}

		return () => {
			document.removeEventListener('mousedown', handleClickOutside);
		};
	}, [isOpen]);

	const handleExport = async (
		type: 'csv' | 'xlsx' | 'summary' | 'pdf' | 'pdf-full',
	) => {
		const timestamp = new Date().toISOString().split('T')[0];
		const baseFilename = `analysis-${analysisData.processingMonth.toLowerCase().replace(' ', '-')}-${timestamp}`;

		switch (type) {
			case 'csv':
				ExportService.exportToCSV(analysisData, `${baseFilename}.csv`);
				break;
			case 'xlsx':
				ExportService.exportToXLSX(analysisData, `${baseFilename}.xlsx`);
				break;
			case 'summary':
				ExportService.exportSummaryToXLSX(
					analysisData,
					`${baseFilename}-summary.xlsx`,
				);
				break;
			case 'pdf':
				await ExportService.exportToPDF(
					analysisData,
					`${baseFilename}-summary.pdf`,
					true,
					fileId,
					'summary',
					viewMode,
				);
				break;
			case 'pdf-full':
				await ExportService.exportToPDF(
					analysisData,
					`${baseFilename}-full.pdf`,
					true,
					fileId,
					'full',
					viewMode,
				);
				break;
		}

		setIsOpen(false);
	};

	return (
		<div ref={dropdownRef} className={`relative ${className}`}>
			<Button
				icon={Download}
				variant="outline"
				onClick={() => setIsOpen(!isOpen)}
				className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-primary-600 hover:text-primary-700 transition-colors font-primary"
			>
				Export Data
				<ChevronDown className="h-4 w-4 ml-2" />
			</Button>

			{isOpen && (
				<>
					{/* Backdrop */}
					<div
						className="fixed inset-0 z-40"
						onClick={() => setIsOpen(false)}
					/>

					{/* Dropdown Menu */}
					<div className="absolute right-0 top-full mt-1 w-56 bg-white border border-neutral-200 rounded-lg shadow-lg z-50">
						<div className="flex flex-col gap-1 justify-start items-start p-1">
							<button
								onClick={() => handleExport('csv')}
								className="flex w-full px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-1"
								type="button"
							>
								<FileText className="w-4 h-4 mr-3 flex-shrink-0" />
								<div className="text-left">
									<div className="font-medium">Export as CSV</div>
									<div className="text-xs text-gray-500">
										All sections in one file
									</div>
								</div>
							</button>

							<button
								onClick={() => handleExport('xlsx')}
								className="flex w-full px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-1"
								type="button"
							>
								<FileSpreadsheet className="w-4 h-4 mr-3 flex-shrink-0" />
								<div className="text-left">
									<div className="font-medium">Export as Excel</div>
									<div className="text-xs text-gray-500">
										All sections with formatting
									</div>
								</div>
							</button>

							<button
								onClick={() => handleExport('summary')}
								className="flex w-full px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-1"
								type="button"
							>
								<FileSpreadsheet className="w-4 h-4 mr-3 flex-shrink-0" />
								<div className="text-left">
									<div className="font-medium">Export Summary</div>
									<div className="text-xs text-gray-500">
										Summary sheet + all data
									</div>
								</div>
							</button>

							<button
								onClick={() => handleExport('pdf')}
								className="flex w-full px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-1"
								type="button"
							>
								<File className="w-4 h-4 mr-3 flex-shrink-0" />
								<div className="text-left">
									<div className="font-medium">Export as PDF (Summary)</div>
									<div className="text-xs text-gray-500">
										Summary view with totals only
									</div>
								</div>
							</button>

							<button
								onClick={() => handleExport('pdf-full')}
								className="flex w-full px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-1"
								type="button"
							>
								<File className="w-4 h-4 mr-3 flex-shrink-0" />
								<div className="text-left">
									<div className="font-medium">Export as PDF (Full)</div>
									<div className="text-xs text-gray-500">
										Complete report with all line items
									</div>
								</div>
							</button>
						</div>
					</div>
				</>
			)}
		</div>
	);
};

export default ExportButton;
